import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Copy, LogOut, Wallet, TrendingUp, CheckCircle2, Link2, ShoppingBag } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { fcfa } from "../../lib/format";
import { QRCodeBox } from "../../components/shop/QRCodeBox";
import { toast } from "sonner";

const PURPLE = "#5B2A9D";

export default function PartnerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/partner/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/partner/orders").then(({ data }) => setOrders(data)).catch(() => {});
  }, []);

  const link = stats ? `${window.location.origin}/boutique?ref=${stats.code}` : "";
  const copy = (txt, msg) => { navigator.clipboard.writeText(txt); toast.success(msg); };
  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-brand-cream">
      <header className="text-white px-4 sm:px-8 h-16 flex items-center justify-between" style={{ background: PURPLE }}>
        <div className="flex items-center gap-2 font-heading font-black text-lg">
          <Wallet className="w-6 h-6 text-brand-gold" /> Espace Partenaire
        </div>
        <button onClick={doLogout} data-testid="partner-logout" className="flex items-center gap-2 text-sm text-white/80 hover:text-white">
          <LogOut className="w-4 h-4" /> Déconnexion
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="partner-dashboard">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-black text-brand-ink">Bonjour {user?.name} 👋</h1>
          <p className="text-brand-sub text-sm mt-1">Partagez votre lien, chaque vente vous rapporte {stats?.commission_rate ?? "…"}% de commission.</p>
        </div>

        {!stats ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-accent" /></div>
        ) : (
          <>
            {/* Lien de partage */}
            <div className="bg-white border border-brand-line rounded-2xl p-5">
              <p className="font-semibold text-brand-ink flex items-center gap-2 mb-3"><Link2 className="w-5 h-5 text-brand-accent" /> Mon lien à partager</p>
              <div className="flex items-center gap-2 flex-wrap">
                <code data-testid="partner-link" className="flex-1 min-w-0 bg-brand-cream rounded-xl px-3 py-2.5 text-sm text-brand-ink break-all">{link}</code>
                <button onClick={() => copy(link, "Lien copié !")} data-testid="copy-partner-link" className="text-white rounded-full px-4 py-2.5 text-sm font-semibold flex items-center gap-1.5 shrink-0" style={{ background: PURPLE }}><Copy className="w-4 h-4" /> Copier</button>
              </div>
              <p className="text-xs text-brand-muted mt-3">Mon code : <b className="text-brand-ink">{stats.code}</b> · Le client peut aussi taper ce code au moment de payer.</p>
              <div className="mt-4 pt-4 border-t border-brand-line flex flex-col items-center">
                <p className="text-sm text-brand-sub mb-3">Ou partagez / imprimez votre QR code 👇</p>
                <QRCodeBox value={link} fileName={`qr-noumabou-${stats.code}`} caption={`Scannez pour acheter · Code ${stats.code}`} />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard icon={ShoppingBag} label="Ventes générées" value={stats.orders_count} sub="commandes" color="#5B2A9D" />
              <StatCard icon={TrendingUp} label="Chiffre d'affaires" value={fcfa(stats.total_sales)} color="#5B2A9D" />
              <StatCard icon={Wallet} label="Commission à recevoir" value={fcfa(stats.commission_due)} color="#C9A227" testid="partner-due" />
              <StatCard icon={CheckCircle2} label="Déjà reçu" value={fcfa(stats.commission_paid)} color="#16a34a" />
            </div>

            {/* Orders */}
            <div className="bg-white border border-brand-line rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-brand-line font-semibold text-brand-ink">Mes ventes</div>
              {orders.length === 0 ? (
                <p className="p-5 text-sm text-brand-muted">Aucune vente pour le moment. Partagez votre lien pour commencer ! 🚀</p>
              ) : (
                <div className="divide-y divide-brand-line">
                  {orders.map((o) => (
                    <div key={o.order_number} data-testid={`partner-order-${o.order_number}`} className="px-5 py-3 flex items-center justify-between text-sm">
                      <div>
                        <p className="font-medium text-brand-ink">{o.order_number}</p>
                        <p className="text-xs text-brand-muted">{new Date(o.created_at).toLocaleDateString("fr-FR")} · {fcfa(o.subtotal)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-brand-ink">{fcfa(o.commission)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${o.commission_paid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{o.commission_paid ? "Payé" : "À recevoir"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div className="bg-white border border-brand-line rounded-2xl p-4" data-testid={testid}>
      <Icon className="w-5 h-5 mb-2" style={{ color }} />
      <p className="text-xs text-brand-muted">{label}</p>
      <p className="font-heading font-black text-lg text-brand-ink">{value}</p>
      {sub && <p className="text-xs text-brand-muted">{sub}</p>}
    </div>
  );
}
